/* ===========================================================================
 * gc-dynamic-badge.js
 * ---------------------------------------------------------------------------
 * Time-based automatic rotation for the product-card message pill.
 *
 * WHY THIS IS JAVASCRIPT AND NOT LIQUID
 * -------------------------------------
 * The obvious build is Liquid: period = floor(now / duration), hash, decide,
 * render. It does not work on Shopify. Storefront HTML is served from a
 * full-page cache, and `{{ 'now' | date: '%s' }}` freezes into that cached
 * output - it reports when the page was last written to cache, not the current
 * time. Merchants have measured Liquid output served from cache 30+ hours
 * later. On a 12h or 24h period that means the rotation would simply not fire
 * on cached pages, and two visitors hitting different cache entries would see
 * different periods - breaking the one rule that matters here, that everyone
 * sees the same thing at the same time.
 *
 * So the clock has to be client-side. Everything else stays deterministic:
 * given (productId, period, seed) every browser on earth computes the same
 * answer, with no network call and no randomness.
 *
 * WHAT IT GUARANTEES
 * ------------------
 *   - Stable for the whole period. No refresh change, no timer, no animation.
 *   - Identical for every visitor in that period (to within clock skew).
 *   - Recomputed only when a new period starts.
 *   - A product's badge, and whether it has one at all, is uncorrelated
 *     between periods - see the note on avalanche in hash() below.
 *   - Nothing is ever un-painted mid-session (see PAINT BUDGET).
 *
 * Config arrives as JSON in #gc-dbadge-config, written by
 * snippets/gc-dynamic-badge-style.liquid. Markup slots are written hidden by
 * snippets/gc-dynamic-badge.liquid; this file only fills and reveals them, so
 * there is no layout shift - the pill is absolutely positioned over the image.
 * =========================================================================== */
(function () {
  'use strict';

  var cfgEl = document.getElementById('gc-dbadge-config');
  if (!cfgEl) return;

  var CFG;
  try { CFG = JSON.parse(cfgEl.textContent); } catch (e) { return; }
  if (!CFG || !CFG.messages || !CFG.messages.length) return;

  var SLOT = '[data-gc-dbadge][data-gc-pid]';
  var GRID = '.halo-column, .halo-row, [data-section-id]';

  /* -----------------------------------------------------------------------
   * cyrb53. Not cryptographic - it does not need to be. The one property that
   * actually matters here is avalanche: period N and period N+1 differ by a
   * single digit in the input, and the outputs must be completely unrelated.
   * A cheaper hash (FNV, or productId * prime + period) fails exactly there -
   * neighbouring inputs stay correlated, so a product selected in one period
   * tends to be selected in the next, and the catalogue never really rotates.
   * That is also why productId % messages.length was wrong: no period term at
   * all, so each product was welded to one message forever.
   * --------------------------------------------------------------------- */
  function hash(str) {
    var h1 = 0xdeadbeef, h2 = 0x41c6ce57, i, ch;
    for (i = 0; i < str.length; i++) {
      ch = str.charCodeAt(i);
      h1 = Math.imul(h1 ^ ch, 2654435761);
      h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return 4294967296 * (2097151 & h2) + (h1 >>> 0);
  }

  /* Periods are anchored to the unix epoch, so they land on clean UTC
   * boundaries: 24h flips at 00:00 UTC, 12h at 00:00 and 12:00, and so on.
   * Predictable, and identical for every visitor regardless of timezone. */
  function currentPeriod() {
    return Math.floor(Date.now() / 1000 / (CFG.periodHours * 3600));
  }

  /* Two independent hashes off one base, so "does it get a badge" and "which
   * message" are decided independently - a product can keep a badge across two
   * periods and still show a different message, and vice versa. */
  function decide(pid, period) {
    var base = pid + '|' + period + '|' + CFG.seed;
    var sel = hash(base + '|sel');
    if (sel % 100 >= CFG.percent) return null;
    return {
      msg: CFG.messages[hash(base + '|msg') % CFG.messages.length],
      rank: sel
    };
  }

  /* The theme's own NEW / SALE / SOLD OUT badges. sale-badge-2 is always in the
   * DOM but empty unless there's a discount, so test for text, not presence. */
  function hasRealBadge(card) {
    var badges = card.querySelectorAll('.product_badges .badge');
    for (var i = 0; i < badges.length; i++) {
      if (badges[i].textContent.trim()) return true;
    }
    return false;
  }

  function reveal(slot, msg, period) {
    slot.textContent = msg;
    slot.removeAttribute('hidden');
    slot.setAttribute('data-gc-done', period);
  }

  function suppress(slot, period) {
    /* Clear the text, don't just hide it. Without this a slot re-decided into a
       new period keeps the previous period's message sitting in the markup -
       invisible, but stale hidden text is worth neither the bytes nor the risk
       of something crawling it. Clearing keeps the DOM a pure function of
       (productId, period). */
    slot.textContent = '';
    slot.setAttribute('hidden', '');
    slot.setAttribute('data-gc-done', period);
  }

  function run() {
    var period = currentPeriod();
    var slots = document.querySelectorAll(SLOT);
    if (!slots.length) return;

    /* Group by grid so "max badges per grid" means per grid, not per page.
     * Falls back to body for a lone card outside any known grid wrapper. */
    var groups = [], keys = [];
    for (var i = 0; i < slots.length; i++) {
      var grid = slots[i].closest(GRID) || document.body;
      var at = keys.indexOf(grid);
      if (at === -1) { keys.push(grid); groups.push([slots[i]]); }
      else { groups[at].push(slots[i]); }
    }

    for (var g = 0; g < groups.length; g++) paintGroup(groups[g], period);
  }

  function paintGroup(list, period) {
    /* PAINT BUDGET
     * Cards already settled this period keep their answer, permanently. Load
     * More and AJAX filtering append cards to a grid that is already painted;
     * if the cap were recomputed over the new, larger set, a badge could be
     * taken away from a card the shopper is looking at. So already-painted
     * cards spend their budget, and only new arrivals compete for what is
     * left, in DOM order - which is deterministic for a given sort. */
    var pending = [], spent = 0, i;

    for (i = 0; i < list.length; i++) {
      var slot = list[i];
      var done = slot.getAttribute('data-gc-done');

      if (done !== null) {
        /* Already settled. Re-decide only if it was settled in an older period
         * - which happens when gc-facets restores a grid snapshot from
         * sessionStorage after the tab has sat open across a boundary. */
        if (done === String(period)) {
          if (!slot.hasAttribute('hidden')) spent++;
          continue;
        }
        slot.removeAttribute('data-gc-done');
      }

      var card = slot.closest('.product-card');
      if (CFG.hideIfBadge && card && hasRealBadge(card)) { suppress(slot, period); continue; }

      var d = decide(slot.getAttribute('data-gc-pid'), period);
      if (!d) { suppress(slot, period); continue; }
      pending.push({ slot: slot, d: d });
    }

    var budget = CFG.maxPerGrid > 0 ? CFG.maxPerGrid - spent : pending.length;

    for (i = 0; i < pending.length; i++) {
      if (i < budget) reveal(pending[i].slot, pending[i].d.msg, period);
      else suppress(pending[i].slot, period);
    }
  }

  /* -----------------------------------------------------------------------
   * Catch cards that arrive after first paint. A MutationObserver is used
   * rather than hooking the theme's fetch calls, because it covers every
   * source without knowing about any of them: AJAX facets, Load More, the
   * sessionStorage grid restore, the predictive-search dropdown, quick view,
   * and any app that injects cards later. All of those swap in server-rendered
   * Liquid, so the slots are already in the markup - they just need deciding.
   *
   * Coalesced into one rAF: a single innerHTML swap of a 24-card grid fires a
   * burst of records, and this collapses them into one pass.
   * --------------------------------------------------------------------- */
  function observe() {
    if (!window.MutationObserver) return;
    var queued = false;
    new MutationObserver(function (records) {
      if (queued) return;
      for (var i = 0; i < records.length; i++) {
        if (records[i].addedNodes.length) {
          queued = true;
          requestAnimationFrame(function () { queued = false; run(); });
          return;
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* -----------------------------------------------------------------------
   * Which surface are we on?
   *
   * theme.liquid writes `<body class="template-{{ request.page_type | handle }}">`,
   * so the page type is already on the body. That is the only reliable place to
   * read it. The obvious alternative - gating in Liquid on template.name inside
   * the slot snippet - breaks on product pages: the recommendations carousel is
   * fetched from routes.product_recommendations_url, and Shopify renders that
   * section in isolation, where the template context is not the product page's.
   * Those cards still land in the product page's DOM though, and that body still
   * says template-product, so reading the body is correct for every path:
   * server-rendered, AJAX, Load More, or injected by an app.
   *
   * Collection and search are always on; only these two are switchable.
   * --------------------------------------------------------------------- */
  function surfaceEnabled() {
    var c = document.body.classList;
    if (c.contains('template-index') && CFG.showHome === false) return false;
    if (c.contains('template-product') && CFG.showProduct === false) return false;
    return true;
  }

  function boot() {
    /* Bail before the observer is attached, so a switched-off surface costs
       nothing at all - no scanning, no listener for the life of the page. */
    if (!surfaceEnabled()) return;
    run();
    observe();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();